import ApiAcces from "@/shared/services/ApiAcces";
import type IAutomationService from "../contracts/IAutomationService";
import type NodeDescriptor from "../models/NodeDescriptor";
import type AutomationSummary from "../models/AutomationSummary";
import type WorkflowDefinition from "../models/WorkflowDefinition";
import type { CreateWorkflowRequest, WorkflowVersionInfo } from "../models/WorkflowDefinition";
import type ExecutionStatus from "../models/ExecutionStatus";
import type ExecutionLogEvent from "../models/ExecutionLogEvent";
import type { WebhookSecret } from "../models/WebhookSecret";

export default class AutomationService
  extends ApiAcces
  implements IAutomationService {
  async GetNodeCatalog(): Promise<NodeDescriptor[]> {
    return (await this.api.get("api/nodes")).data;
  }

  async GetAll(): Promise<AutomationSummary[]> {
    return (await this.api.get("api/workflows")).data;
  }

  async GetWorkflow(workflowId: string): Promise<WorkflowDefinition | null> {
    try {
      return (await this.api.get(`api/workflows/${workflowId}`)).data;
    } catch (err) {
      if ((err as { status?: number })?.status === 404) return null;
      throw err;
    }
  }

  async Publish(request: CreateWorkflowRequest): Promise<WorkflowVersionInfo> {
    return (await this.api.post("api/workflows", request)).data;
  }

  async Delete(workflowId: string): Promise<void> {
    await this.api.delete(`api/workflows/${workflowId}`);
  }

  async Execute(
    workflowId: string,
    triggerPayload?: Record<string, unknown>,
  ): Promise<ExecutionStatus> {
    return (
      await this.api.post(`api/workflows/${workflowId}/executions`, {
        triggerPayload,
      })
    ).data;
  }

  async GetExecutionStatus(
    workflowId: string,
    executionId: string,
  ): Promise<ExecutionStatus> {
    return (
      await this.api.get(
        `api/workflows/${workflowId}/executions/${executionId}`,
      )
    ).data;
  }

  async GetExecutions(workflowId: string, limit = 50): Promise<ExecutionStatus[]> {
    return (
      await this.api.get(`api/workflows/${workflowId}/executions`, {
        params: { limit },
      })
    ).data;
  }

  async GetExecutionLogs(
    workflowId: string,
    executionId: string,
  ): Promise<ExecutionLogEvent[]> {
    return (
      await this.api.get(
        `api/workflows/${workflowId}/executions/${executionId}/logs`,
      )
    ).data;
  }

  async CreateOrRotateWebhook(workflowId: string): Promise<WebhookSecret> {
    return (await this.api.post(`api/workflows/${workflowId}/webhook`)).data;
  }

  async DisableWebhook(workflowId: string): Promise<void> {
    await this.api.delete(`api/workflows/${workflowId}/webhook`);
  }
}
